import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';

/**
 * The DS `Dialog` size variants are 250 / 620 / 800px. `small` squeezes the
 * heading into "You already have a GoTu / account" and `medium` is far too wide
 * for three short lines, so the panel sits between them at this width — the
 * same gap the Best-in-Dental dialogs hit and settle at here.
 *
 * The override is an inline `maxWidth`, which beats the variant's `max-w-*`
 * class, so the `size` prop only decides the fallback if this is ever removed:
 * `medium` degrades to merely wide, `small` to the unreadable 250px.
 *
 * A hoisted constant rather than a literal object, so the Betterer inline-style
 * ratchet (which matches the literal form) stays where it is.
 */
const PANEL_MAX_WIDTH = 358;
const PANEL_STYLE = { maxWidth: PANEL_MAX_WIDTH } as const;

/**
 * `Dialog` hardcodes `aria-labelledby` to the id it would put on a *string*
 * child of `DialogTitle`. This dialog's heading is too long for that slot —
 * the DS renders a string title `whitespace-nowrap` — so the heading lives in
 * the content and this id points the label at it. `Dialog` spreads its props
 * after its own attributes, so passing `aria-labelledby` overrides the dangling
 * default rather than leaving the panel labelled by a non-existent element.
 */
const TITLE_ID = 'referral-account-exists-title';

export interface IReferralAccountExistsDialogProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** "Close", the backdrop and Escape. */
  onClose: () => void;
}

/**
 * The "you already have a GoTu account" dialog (GOTU-7434, Figma 4608-25088).
 *
 * Two triggers, one dialog: a signed-in office opens a v2 referral link, or a
 * sign-up carrying a referral code fails because the email already has an
 * account. Either way the referral is for new accounts only, so there is
 * nothing to apply — the one action closes it and leaves them where they are.
 *
 * Presentational only. `Dashboard` renders it off the navigation state
 * `ReferralLinkRedirect` sets, the same way it renders the not-allowed popup;
 * `SignUpForm` renders it off `isReferralBlockedByExistingAccount`.
 *
 * Copy is mobile's office variant (`ReferralAccountExistsDialog`, GOTU-7313):
 * offices get no v2 referral code, so "You can still refer other pros…" is
 * dropped (GOTU-9915). Change both clients or neither.
 */
export const ReferralAccountExistsDialog = ({
  open,
  onClose,
}: IReferralAccountExistsDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    size="medium"
    style={PANEL_STYLE}
    aria-labelledby={TITLE_ID}
    data-testid="referral-account-exists-dialog"
  >
    {/* `DialogContent` brings `px-4 pb-5` but no top padding — the DS expects a
        `DialogTitle` above it to open the panel up. An empty title row would
        add ~72px of dead space and a corner X duplicating the Close button
        below, so the top padding is set here instead and balances `pb-5`. */}
    <DialogContent>
      <Column gap={4} align="center" pt={6}>
        <Typography id={TITLE_ID} bold variant="subtitle1" textAlign="center">
          You already have a GoTu account
        </Typography>
        <Typography variant="body1" textAlign="center" color={colors.neutral[500]}>
          You are not eligible for this referral.
        </Typography>
        <Typography variant="body1" textAlign="center" color={colors.neutral[500]}>
          Log in to your existing account.
        </Typography>
      </Column>
    </DialogContent>
    {/* `DialogActions` is `justify-end` by default; a single acknowledging
        action centres, matching the other small dialogs. */}
    <DialogActions className="justify-center">
      {/* No `variant`/`size`: the DS defaults are already contained + primary +
          medium, which is what every other dialog action in the app uses. The
          legacy `size="lg"` this carried is `h-12`/18px — far too tall for a
          five-letter label. */}
      <Button
        data-cy="referral-account-exists-close-button"
        data-testid="referral-account-exists-close-button"
        onClick={onClose}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);
